// Class Subject Associations CRUD operations using direct fetch

/// <reference lib="dom" />

import { handleApiCall } from './errors'
import { getSchoolContext } from './school-context'
import { getAccessToken } from './auth-token'
import type {
  ClassSubjectAssociation,
  CreateClassSubjectAssociationRequest,
  ClassSubjectAssociationsFilters,
  Subject,
} from '@timetable/types'
import { getSubjectsDirect } from './subjects-direct'

// Get environment variables
const SUPABASE_URL = (import.meta as any).env?.VITE_SUPABASE_URL as string
const SUPABASE_ANON_KEY = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY as string

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error('Missing Supabase environment variables')
}

/**
 * Get class subject associations with optional filters
 * Requirements: 2.1, 2.2, 8.1
 */
export async function getClassSubjectAssociations(
  filters: ClassSubjectAssociationsFilters = {}
): Promise<ClassSubjectAssociation[]> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const params = new URLSearchParams()
    params.append('select', '*')

    const schoolId = filters.school_id || getSchoolContext()
    if (schoolId) {
      params.append('school_id', `eq.${schoolId}`)
    }
    if (filters.class_id) {
      params.append('class_id', `eq.${filters.class_id}`)
    }
    if (filters.subject_id) {
      params.append('subject_id', `eq.${filters.subject_id}`)
    }

    const response = await fetch(`${SUPABASE_URL}/rest/v1/class_subjects?${params}`, {
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to fetch class subject associations')
    }

    return await response.json()
  })
}

/**
 * Create a class subject association
 * Requirements: 2.3, 2.4, 8.2
 */
export async function createClassSubjectAssociation(
  req: CreateClassSubjectAssociationRequest
): Promise<ClassSubjectAssociation> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(`${SUPABASE_URL}/rest/v1/class_subjects`, {
      method: 'POST',
      headers: {
        'apikey': SUPABASE_ANON_KEY,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        'Prefer': 'return=representation',
      },
      body: JSON.stringify(req),
    })

    if (response.status === 409) {
      throw new Error('Association already exists')
    }

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to create class subject association')
    }

    const data: ClassSubjectAssociation[] = await response.json()
    return data[0]
  })
}

/**
 * Delete a class subject association by id
 * Requirements: 2.5, 8.3
 */
export async function deleteClassSubjectAssociation(id: string): Promise<void> {
  return handleApiCall(async () => {
    const accessToken = getAccessToken()
    if (!accessToken) throw new Error('Not authenticated')

    const response = await fetch(
      `${SUPABASE_URL}/rest/v1/class_subjects?id=eq.${encodeURIComponent(id)}`,
      {
        method: 'DELETE',
        headers: {
          'apikey': SUPABASE_ANON_KEY,
          'Authorization': `Bearer ${accessToken}`,
        },
      }
    )

    // Treat 404 or empty response as success
    if (response.status === 404 || response.status === 204) {
      return
    }

    if (!response.ok) {
      const error = await response.json()
      throw new Error(error.message || 'Failed to delete class subject association')
    }
  })
}

/**
 * Get subjects available for a class.
 * If the class has subject associations, returns only those subjects.
 * If no associations exist, returns all active subjects for the school (unrestricted path).
 * Requirements: 3.1, 3.2, 3.3, 3.4
 */
export async function getSubjectsForClass(classId: string, schoolId?: string): Promise<Subject[]> {
  return handleApiCall(async () => {
    const resolvedSchoolId = schoolId || getSchoolContext() || undefined

    const associations = await getClassSubjectAssociations({
      class_id: classId,
      school_id: resolvedSchoolId,
    })

    if (associations.length === 0) {
      // Unrestricted path: return all active subjects for the school
      return getSubjectsDirect({ school_id: resolvedSchoolId })
    }

    // Restricted path: return only subjects linked to this class
    const subjectIds = new Set(associations.map((a) => a.subject_id))
    const allSubjects = await getSubjectsDirect({ school_id: resolvedSchoolId })
    return allSubjects.filter((s) => subjectIds.has(s.id))
  })
}
